import { PermissionGuard } from '@/components/PermissionGuard';
import { TravelEsimQrCodeDialog } from '@/components/TravelEsimQrCodeDialog';
import { useResendTravelEsimQrCode } from '@/hooks/useTravelEsimOrderActions';
import { useTravelEsimQrCodeUrlById } from '@/hooks/useTravelEsimOrders';
import { formatDateShort, formatDateTime } from '@/lib/format';
import { TRAVEL_ESIM_INSTALL_STEPS } from '@/lib/travel-esim-guide';
import type { TravelEsimDetail } from '@octave/contracts';
import { Calendar, Hash, Loader2, Mail, QrCode, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { DetailRow } from './DetailRow';

interface LineTravelEsimCardProps {
  esim: TravelEsimDetail;
}

/**
 * The eSIM itself, on the « Carte SIM » card model: number, type, creation,
 * then the « Code d'activation eSIM » block with the QR code shown inline,
 * the e-mail resend and the install instructions beside it.
 */
export function LineTravelEsimCard({ esim }: LineTravelEsimCardProps) {
  const [enlarged, setEnlarged] = useState(false);
  const resend = useResendTravelEsimQrCode(esim.id);
  const sent = !!esim.qrCodeSentAt;
  const { data: qrCode, isLoading: isQrLoading } = useTravelEsimQrCodeUrlById(
    esim.id,
    sent,
  );

  return (
    <div className="flex h-full flex-col rounded-2xl bg-white p-6 shadow-sm">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-900">eSIM internationale</h2>
        <p className="text-xs text-slate-400">Informations et code d'activation</p>
      </div>

      <div className="space-y-3">
        <DetailRow
          icon={Hash}
          label="Numéro eSIM"
          value={esim.esimNumber ?? 'À venir'}
          mono={!!esim.esimNumber}
        />
        <DetailRow icon={Smartphone} label="Type" value="eSIM internationale" />
        <DetailRow
          icon={Calendar}
          label="Création"
          value={formatDateShort(esim.createdAt)}
        />
      </div>

      {/* Activation block, on the model of the classic eSIM card */}
      <div className="mt-6 flex-1 rounded-xl border border-slate-100 p-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-700">
            Code d'activation eSIM
          </h3>
          {sent && (
            <PermissionGuard permission="write">
              <button
                type="button"
                onClick={() => resend.mutate()}
                disabled={resend.isPending}
                className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-600 transition-colors hover:bg-emerald-100 disabled:opacity-60 cursor-pointer"
              >
                {resend.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Mail className="h-3.5 w-3.5" />
                )}
                Envoyer par email
              </button>
            </PermissionGuard>
          )}
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-center text-xs text-slate-500">
            {sent
              ? `QR code envoyé le ${formatDateTime(esim.qrCodeSentAt ?? '')}`
              : 'QR code en attente : Octave vous l’envoie après validation de la commande.'}
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-6 md:flex-row">
          <div className="flex-shrink-0 flex justify-center">
            {sent && qrCode ? (
              <button
                type="button"
                onClick={() => setEnlarged(true)}
                title="Agrandir le QR code"
                className="rounded-xl border border-slate-100 bg-white p-3 cursor-zoom-in"
              >
                <img
                  src={qrCode.url}
                  alt="QR code de l’eSIM internationale"
                  className="h-[180px] w-[180px] object-contain"
                />
              </button>
            ) : (
              <div className="flex h-[204px] w-[204px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50 text-slate-400">
                {isQrLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <QrCode className="h-6 w-6" />
                    <span className="text-xs">QR code en attente</span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 space-y-3">
            <h4 className="text-sm font-semibold text-slate-700">Instructions</h4>
            <div className="space-y-2 text-sm">
              {TRAVEL_ESIM_INSTALL_STEPS.map((item) => (
                <div key={item.device}>
                  <p className="mb-1 font-medium text-slate-700">{item.device} :</p>
                  <p className="text-slate-400">{item.steps}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {enlarged && (
        <TravelEsimQrCodeDialog
          esimId={esim.id}
          title={`eSIM de ${esim.beneficiary.name}`}
          onClose={() => setEnlarged(false)}
        />
      )}
    </div>
  );
}
